import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  where,
} from 'firebase/firestore'

import { DocumentDoesNotExistError } from 'utils/error/DocumentDoesNotExistError'
import { db } from 'utils/firebase'
import { formatDocSnapshot, formatQuerySnapshot } from 'utils/queries/formatFirestoreResults'
import { formatQueryOptions } from 'utils/queries/formatQueryOptions'
import { generateReferenceIds, generateRoomName } from 'utils/string'
import { createPartialUser } from 'utils/user/createPartialUser'

import { Message, Room, RoomTypes } from 'types/chat'
import { Program } from 'types/program'
import { MultiItemQuery, Options, SingleItemQuery } from 'types/query'
import { PartialUser, UserType } from 'types/user'

import { fetchUserByUserId } from './users'

export const createPostRoom = async ({ postId, user }: { postId: string; user: PartialUser }) => {
  try {
    const existingRoom = await fetchRoomByPostId({ postId })

    if (existingRoom) {
      return { id: existingRoom.id }
    }
  } catch (error) {
    if ((error as { type?: string })?.type !== DocumentDoesNotExistError.type) {
      throw error
    }
  }

  const room: Omit<Room, 'id' | 'lastMessage'> & {
    lastMessage: Omit<Message, 'id'>
  } = {
    avatarUsers: [],
    created: Date.now(),
    lastMessage: {
      body: '',
      created: Date.now(),
      creator: user,
    },
    name: '',
    reference: { ids: [], type: RoomTypes.POST, postId },
  }

  const roomRef = await addDoc(collection(db, 'rooms'), room)
  return { id: roomRef.id }
}

export const createCompletionRoom = async ({
  completionId,
  user,
}: {
  completionId: string
  user: PartialUser
}) => {
  try {
    const existingRoom = await fetchRoomByCompletionId({ completionId })

    if (existingRoom) {
      return { id: existingRoom.id }
    }
  } catch (error) {
    if ((error as { type?: string })?.type !== DocumentDoesNotExistError.type) {
      throw error
    }
  }

  const room: Omit<Room, 'id' | 'lastMessage'> & {
    lastMessage: Omit<Message, 'id'>
  } = {
    avatarUsers: [],
    created: Date.now(),
    lastMessage: {
      body: '',
      created: Date.now(),
      creator: user,
    },
    name: '',
    reference: { ids: [], type: RoomTypes.COMPLETION, completionId },
  }

  const roomRef = await addDoc(collection(db, 'rooms'), room)
  return { id: roomRef.id }
}

export const createRoom = async ({ participants }: { participants: PartialUser[] }) => {
  try {
    const existingRoom = await fetchRoomByParticipants({ participants })

    if (existingRoom) {
      return { id: existingRoom.id }
    }
  } catch (error) {
    if ((error as { type?: string })?.type !== DocumentDoesNotExistError.type) {
      throw error
    }
  }

  const room: Omit<Room, 'id' | 'lastMessage'> & {
    lastMessage: Omit<Message, 'id'>
  } = {
    avatarUsers: participants.slice(0, 4),
    created: Date.now(),
    lastMessage: {
      body: '',
      created: Date.now(),
      creator: participants[0],
    },
    name: generateRoomName(participants),
    reference: { ids: generateReferenceIds(participants), type: RoomTypes.DIRECT },
  }

  const roomRef = await addDoc(collection(db, 'rooms'), room)
  const participantsRef = collection(db, 'rooms', roomRef.id, 'participants')

  const participantPromises = participants.map((participant) => {
    const participantRef = doc(participantsRef, participant.id)

    return setDoc(participantRef, {
      user: participant,
      lastViewed: Date.now(),
    } as { user: PartialUser; lastViewed: Required<Room>['meta']['lastViewed'] })
  })

  await Promise.all(participantPromises)

  return { id: roomRef.id }
}

export const createProgramRoom = async ({ programId }: { programId: string }) => {
  try {
    const existingRoom = await fetchRoomByProgramId({ programId })

    if (existingRoom) {
      return { id: existingRoom.id }
    }
  } catch (error) {
    if ((error as { type?: string })?.type !== DocumentDoesNotExistError.type) {
      throw error
    }
  }

  const programRef = doc(db, 'programs', programId)
  const programSnapshot = await getDoc(programRef)
  const programData = programSnapshot.data() as Program | undefined

  if (!programData) {
    throw new Error('Program not found, please try again')
  }

  if (!programData.trainer.id) {
    throw new Error('Unable to create a room for this program, please try again')
  }

  const userRef = doc(db, 'users', programData.trainer.id)
  const userSnapshot = await getDoc(userRef)
  const userData = userSnapshot.data() as UserType

  const partialCoach = createPartialUser(userData)

  const room: Omit<Room, 'id' | 'lastMessage'> & {
    lastMessage: Omit<Message, 'id'>
  } = {
    avatarUsers: [partialCoach],
    created: Date.now(),
    lastMessage: {
      body: '',
      created: Date.now(),
      creator: partialCoach,
    },
    name: `${partialCoach.username}'s Group`,
    reference: {
      type: RoomTypes.PROGRAM,
      ids: [programId],
    },
  }

  const roomRef = await addDoc(collection(db, 'rooms'), room)

  return { id: roomRef.id }
}

export const updateLastViewed = async ({ roomId, userId }: { roomId: string; userId: string }) => {
  const participantDoc = doc(db, 'rooms', roomId, 'participants', userId)

  return setDoc(participantDoc, { lastViewed: Date.now() }, { merge: true })
}

export const fetchRoom = async ({ roomId }: { roomId: string }): Promise<Room> => {
  const roomDoc = doc(db, 'rooms', roomId)
  const roomSnapshot = await getDoc(roomDoc)
  return formatDocSnapshot<Room>(roomSnapshot)
}

export const fetchRoomByPostId: SingleItemQuery<{ postId: string }, Room> = async (
  { postId },
  options = { limit: 1 },
) => {
  const roomQuery = query(
    collection(db, 'rooms'),
    where('reference.postId', '==', postId),
    where('reference.type', '==', RoomTypes.POST),
  )
  const queryWithOptions = formatQueryOptions(roomQuery, options)
  const roomSnapshot = await getDocs(queryWithOptions)
  const room = roomSnapshot.docs[0]
  return formatDocSnapshot<Room>(room)
}

export const fetchRoomByCompletionId: SingleItemQuery<{ completionId: string }, Room> = async (
  { completionId },
  options = { limit: 1 },
) => {
  const roomQuery = query(
    collection(db, 'rooms'),
    where('reference.completionId', '==', completionId),
    where('reference.type', '==', RoomTypes.COMPLETION),
  )
  const queryWithOptions = formatQueryOptions(roomQuery, options)
  const roomSnapshot = await getDocs(queryWithOptions)
  const room = roomSnapshot.docs[0]
  return formatDocSnapshot<Room>(room)
}

export const fetchRoomByProgramId: SingleItemQuery<{ programId: string }, Room> = async (
  { programId },
  options = { limit: 1, order: 'created', orderDirection: 'asc' },
) => {
  const roomQuery = query(
    collection(db, 'rooms'),
    where('reference.ids', 'array-contains', programId),
    where('reference.type', '==', RoomTypes.PROGRAM),
  )
  const queryWithOptions = formatQueryOptions(roomQuery, options)
  const roomSnapshot = await getDocs(queryWithOptions)
  const room = roomSnapshot.docs[0]
  return formatDocSnapshot<Room>(room)
}

export const fetchRoomByParticipants: SingleItemQuery<
  { participants: PartialUser[] },
  Room
> = async (
  { participants },
  options = { limit: 1, order: 'created', orderDirection: 'asc' },
): Promise<Room> => {
  const referenceIds = generateReferenceIds(participants)
  const baseQuery = query(
    collection(db, 'rooms'),
    where('reference.ids', '==', referenceIds),
    where('reference.type', '==', RoomTypes.DIRECT),
  )
  const queryWithOptions = formatQueryOptions(baseQuery, options)
  const roomSnapshot = await getDocs(queryWithOptions)
  const room = roomSnapshot.docs[0]
  return formatDocSnapshot<Room>(room)
}

export const fetchUserRooms: MultiItemQuery<{ userId: string }, Room> = async (
  { userId },
  options = { limit: 20, order: 'lastMessage.created' as any },
) => {
  // Get all direct rooms for this user
  const baseQuery = query(
    collection(db, 'rooms'),
    where('reference.type', '==', RoomTypes.DIRECT),
    where('reference.ids', 'array-contains', userId),
  )
  const queryWithOptions = formatQueryOptions(baseQuery, options)
  const roomSnapshot = await getDocs(queryWithOptions)

  if (!roomSnapshot) return Promise.resolve([])

  // Get user participant data for each room, extract lastViewed and messageCount
  const fullRoomPromises = roomSnapshot.docs.map(async (room) => {
    const participantSnapshot = await getDoc(doc(db, 'rooms', room.id, 'participants', userId))
    const participant = participantSnapshot.data() as { lastViewed: number }

    const newMessageCount = await fetchUnreadMessageCount({
      roomId: room.id,
      lastViewed: participant.lastViewed,
    })

    return {
      ...formatDocSnapshot<Room>(room),
      meta: { lastViewed: participant.lastViewed, newMessageCount },
    }
  })

  return Promise.all(fullRoomPromises)
}

export const fetchUpspaceRoom = async ({
  roomId,
  userId,
}: {
  roomId: string
  userId: string
}): Promise<Room | null> => {
  const room = await fetchRoom({ roomId })
  const participantSnapshot = await getDoc(doc(db, 'rooms', roomId, 'participants', userId))

  if (!participantSnapshot) return Promise.resolve(null)

  const participant = participantSnapshot.data() as { lastViewed: number }

  const newMessageCount = await fetchUnreadMessageCount({
    roomId,
    lastViewed: participant?.lastViewed ?? 0,
  })

  return {
    ...room,
    meta: { lastViewed: participant?.lastViewed ?? 0, newMessageCount },
  }
}

export const createMessage = async ({
  messageId,
  messageBody,
  roomId,
  user,
}: {
  messageId: string
  messageBody: string
  roomId: string
  user: UserType
}) => {
  const message: Omit<Message, 'id'> = {
    body: messageBody,
    created: Date.now(),
    creator: createPartialUser(user),
  }

  runTransaction(db, async () => {
    await setDoc(doc(db, 'rooms', roomId, 'messages', messageId), message)
    await setDoc(doc(db, 'rooms', roomId), { lastMessage: message }, { merge: true })
  })
}

export const fetchMessages: MultiItemQuery<
  { roomId: string; blockedCreatorIds?: string[]; currentUserId?: string },
  Message
> = async (
  { roomId, blockedCreatorIds, currentUserId },
  options = { order: 'created', limit: 20 },
) => {
  const messagesQuery = query(collection(db, 'rooms', roomId, 'messages'))

  const messagesQueryWithOptions = formatQueryOptions(messagesQuery, options)
  const messagesSnapshot = await getDocs(messagesQueryWithOptions)

  let messages: Message[] = formatQuerySnapshot(messagesSnapshot)

  if (currentUserId) {
    if (blockedCreatorIds && blockedCreatorIds.length > 0) {
      messages = messages.filter(
        (message) => !blockedCreatorIds.includes(message.creator.id as string),
      )
    }

    const creatorIds = messages.map((message) => message.creator.id)
    const uniqueCreatorIds = creatorIds.filter(
      (value, index, self) => self.indexOf(value) === index,
    )

    const fullCreators: UserType[] = []
    const creatorPromises = uniqueCreatorIds.map((id) =>
      fetchUserByUserId({
        userId: id,
      })
        .then((creator) => {
          if (creator) {
            fullCreators.push(creator)
          }
        })
        .catch(() => console.error(`User ${id} doesn't exist anymore.`)),
    )

    await Promise.all(creatorPromises)

    fullCreators.forEach((fullCreator) => {
      if (fullCreator?.blockedUserIds && fullCreator?.blockedUserIds?.length > 0) {
        if (fullCreator.blockedUserIds.indexOf(currentUserId) > -1) {
          const messagesByCreator: Message[] = messages.filter(
            (message) => message.creator.id === fullCreator.id,
          )

          messagesByCreator.forEach((creatorMessage) => {
            messages = messages.filter((message) => message.id !== creatorMessage.id)
          })
        }
      }
    })
  }

  return messages
}

// Fetch the number of messages since the last time the user viewed the room
export const fetchUnreadMessageCount = async (
  {
    roomId,
    lastViewed,
  }: {
    roomId: string
    lastViewed: number
  },
  options: Options<number> = { limit: 10 },
) => {
  const messagesQuery = query(
    collection(db, 'rooms', roomId, 'messages'),
    where('created', '>', lastViewed),
  )

  const messagesQueryWithOptions = formatQueryOptions(messagesQuery, options)
  const messagesSnapshot = await getDocs(messagesQueryWithOptions)

  return messagesSnapshot.docs.length
}
